//-----------------------------------------------------------------------------
//
// seo.js
//
//-----------------------------------------------------------------------------

// Imports
import * as React from "react"
import PropTypes from "prop-types"
import { useStaticQuery, graphql } from "gatsby"


//-----------------------------------------------------------------------------
//
// SEO
//
//-----------------------------------------------------------------------------

// Function Seo
function Seo({ description, keywords, title, children, titleMinimize=false, home=false }) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            keywords
          }
        }
      }
    `
  )

  // Get default entries (if needed)
  const metaDescription = description || site.siteMetadata.description
  const metaKeywords = keywords || site.siteMetadata.keywords
  const defaultTitle = site.siteMetadata?.title

  // Create structured data JSON+LD schema
  const jsonldWebsite = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Sprucely.io",
    "url": "https://www.sprucely.io/"
  };

  const jsonldWebpage = {
    "@context": "http://schema.org",
    "@type": "WebPage",
    "name": (defaultTitle && !titleMinimize) ? `${title} | ${defaultTitle}` : title,
    "description": metaDescription
  };

  // Stringify the schema object (adding the "null, 2" gives you readable json)
  const websiteAsString = JSON.stringify(jsonldWebsite, null, 2);
  const webpageAsString = JSON.stringify(jsonldWebpage, null, 2);

  // Render
  return (
    <>
      <title>{(defaultTitle && !titleMinimize) ? `${title} | ${defaultTitle}` : title}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Sprucely" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:image" content="https://www.sprucely.io/cross-origin/invitation-overview.png" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:creator" content={site.siteMetadata?.author || ``} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={metaDescription} />
      {(home) &&<script type="application/ld+json">{websiteAsString}</script>}
      <script type="application/ld+json">{webpageAsString}</script>
      {children}
    </>
  )
}


// Default properties
Seo.defaultProps = {
  description: ``,
}


// Property definitions
Seo.propTypes = {
  description: PropTypes.string,
  title: PropTypes.string.isRequired,
}


// Default export
export default Seo
